// Consumer session management
// Story 4.2: Chat Interface for Asking Questions
//
// Design decision: localStorage vs sessionStorage
// We use localStorage intentionally so that:
// 1. User can close tab and return later to continue conversation
// 2. Conversation history is preserved across browser sessions
// 3. Analytics can track returning users to the same bot
// If per-tab isolation is needed, switch to sessionStorage.

const SESSION_STORAGE_KEY = 'boty_consumer_session';

function generateFallbackUUID(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes.set([((bytes[6] ?? 0) & 0x0f) | 0x40], 6); // version 4
  bytes.set([((bytes[8] ?? 0) & 0x3f) | 0x80], 8); // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Get or create a session ID for the consumer
 * Uses localStorage to persist session across browser sessions
 * (See module comment for design rationale)
 *
 * @returns Session ID string (empty string during SSR)
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return ''; // SSR - return empty, client will hydrate
  }

  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID?.() ?? generateFallbackUUID();
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Clear the current session
 * Useful for testing or when user wants to start fresh
 */
export function clearSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

/**
 * Get current session ID without creating one
 *
 * @returns Session ID if exists, null otherwise
 */
export function getCurrentSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(SESSION_STORAGE_KEY);
}
